import { Mail, ShieldCheck, UserMinus, UserPlus, UserX } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  BULK_USER_ACTION_LABELS,
  type BulkUsersInput,
  type BulkUsersResponse,
  type RoleAssignmentInput,
  type UserListFilter,
} from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Field } from '../../../../components/Field';
import { PersonPicker } from '../../../../components/PersonPicker';
import { Popover, usePopover } from '../../../../components/Popover';
import { formatNumber } from '../../../../lib/format';
import { selectionCount, type SelectionState } from '../../../../lib/selection';
import { useToasts } from '../../../../stores/toasts';
import { useBulkUsers, useColleagues } from '../../api/users-api';
import { toUserSelection } from './people-address';
import { EMPTY_ROLE_REACH, RoleReachFields, toAssignment, type RoleReachDraft } from './RoleReachFields';
import { bulkTotals } from './ResultList';
import styles from './PeopleBulkActions.module.css';

/**
 * USER-01's bulk actions on the people ticked, or on "all N matching" (the list's filter less those unticked since,
 * 06-qol §3). The server does each person as the one-person action, with its own rules and its own event, and says
 * per person what happened; that summary is handed to the page to show.
 */
export function PeopleBulkActions({
  selection,
  filter,
  total,
  onResult,
}: {
  readonly selection: SelectionState;
  readonly filter: UserListFilter;
  readonly total: number | undefined;
  readonly onResult: (title: string, response: BulkUsersResponse) => void;
}): ReactNode {
  const bulk = useBulkUsers();
  const showToast = useToasts((state) => state.show);
  const showUndo = useToasts((state) => state.showUndo);
  const { isOpen: isRoleOpen, anchor: roleAnchor, triggerRef: roleRef, toggle: toggleRole, close: closeRole } = usePopover();
  const {
    isOpen: isDeactivateOpen,
    anchor: deactivateAnchor,
    triggerRef: deactivateRef,
    toggle: toggleDeactivate,
    close: closeDeactivate,
  } = usePopover();
  const target = toUserSelection(selection, filter);
  const count = selectionCount(selection, total);
  const people = count === undefined ? 'everyone matching' : `${formatNumber(count)} ${count === 1 ? 'person' : 'people'}`;

  if (target === null) return null;

  const run = (input: BulkUsersInput, onDone?: () => void): void => {
    const title = BULK_USER_ACTION_LABELS[input.action];
    bulk.mutate(input, {
      onSuccess: (response) => {
        onDone?.();
        const message = `${title}: ${bulkTotals(response)}`;
        const undo = panelUndoOf(input, response);
        // Review R25: a panel change is reversible, so it gets Undo (instant-feel rule 8), reversing exactly the people it changed.
        if (undo) {
          showUndo(message, () =>
            bulk.mutate(undo, {
              onSuccess: (undone) => onResult(`Undone: ${title}`, undone),
              onError: (error) => showToast({ tone: 'danger', message: error.message }),
            }),
          );
        }
        else showToast({ tone: response.failed > 0 ? 'warning' : 'success', message });
        onResult(title, response);
      },
      onError: (error) => showToast({ tone: 'danger', message: error.message }),
    });
  };

  return (
    <>
      <Button ref={roleRef} size="sm" variant="ghost" icon={ShieldCheck} onClick={toggleRole} aria-expanded={isRoleOpen}>
        Change role
      </Button>
      <Button size="sm" variant="ghost" icon={UserPlus} disabled={bulk.isPending} onClick={() => run({ action: 'add_to_panel', selection: target })}>
        Add to panels
      </Button>
      <Button size="sm" variant="ghost" icon={UserMinus} disabled={bulk.isPending} onClick={() => run({ action: 'remove_from_panel', selection: target })}>
        Remove from panels
      </Button>
      <Button size="sm" variant="ghost" icon={Mail} disabled={bulk.isPending} onClick={() => run({ action: 'resend_invite', selection: target })}>
        Resend invites
      </Button>
      <Button
        ref={deactivateRef}
        size="sm"
        variant="danger"
        icon={UserX}
        onClick={toggleDeactivate}
        aria-expanded={isDeactivateOpen}
      >
        Deactivate…
      </Button>

      <Popover anchor={roleAnchor} onClose={closeRole} width={360} label="Change role">
        <ChangeRoleForm
          people={people}
          isPending={bulk.isPending}
          onCancel={closeRole}
          onApply={(assignment) => run({ action: 'set_role', selection: target, assignment }, closeRole)}
        />
      </Popover>

      <Popover anchor={deactivateAnchor} onClose={closeDeactivate} align="right" width={360} label="Deactivate">
        <BulkDeactivateForm
          people={people}
          isPending={bulk.isPending}
          onCancel={closeDeactivate}
          onApply={(reassignToUserId) =>
            run(
              { action: 'deactivate', selection: target, ...(reassignToUserId ? { reassignToUserId } : {}) },
              closeDeactivate,
            )
          }
        />
      </Popover>
    </>
  );
}

function ChangeRoleForm({
  people,
  isPending,
  onCancel,
  onApply,
}: {
  readonly people: string;
  readonly isPending: boolean;
  readonly onCancel: () => void;
  readonly onApply: (assignment: RoleAssignmentInput) => void;
}): ReactNode {
  const [draft, setDraft] = useState<RoleReachDraft>(EMPTY_ROLE_REACH);
  const assignment = toAssignment(draft);
  return (
    <div className={styles.form}>
      <h3 className={styles.title}>Change the role of {people}</h3>
      <p className={styles.body}>
        <strong>This replaces every role each of them has</strong> with this one role and reach. Anyone who already has
        exactly this is left as they are.
      </p>
      <RoleReachFields value={draft} onChange={setDraft} />
      <div className={styles.actions}>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" disabled={assignment === null} isPending={isPending} onClick={() => assignment && onApply(assignment)}>
          Replace their roles
        </Button>
      </div>
    </div>
  );
}

/** One person takes over everybody's work; anyone who owns work while nobody is named is skipped, and said so. */
function BulkDeactivateForm({
  people,
  isPending,
  onCancel,
  onApply,
}: {
  readonly people: string;
  readonly isPending: boolean;
  readonly onCancel: () => void;
  readonly onApply: (reassignToUserId: string | null) => void;
}): ReactNode {
  const colleagues = useColleagues();
  const [successor, setSuccessor] = useState<string | null>(null);
  return (
    <div className={styles.form}>
      <h3 className={styles.title}>Deactivate {people}?</h3>
      <p className={styles.body}>
        They are signed out everywhere and can't sign in again. The hire requests and jobs they recruit for, the jobs
        they line-manage and their open tasks move to the person you choose first. A hire request they raised stays
        theirs. You can't pick someone who is being deactivated.
      </p>
      <Field label="Who takes over their work" hint="Anyone with work and nobody to take it is skipped.">
        {({ inputId, describedBy }) => (
          <PersonPicker
            id={inputId}
            describedBy={describedBy}
            label="Who takes over their work"
            people={colleagues.data ?? []}
            value={successor}
            onChange={setSuccessor}
            placeholder="Choose a colleague"
            isClearable
          />
        )}
      </Field>
      <p className={styles.note}>Reactivating someone later doesn't give their work back.</p>
      <div className={styles.actions}>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="dangerSolid" icon={UserX} isPending={isPending} onClick={() => onApply(successor)}>
          Deactivate {people}
        </Button>
      </div>
    </div>
  );
}

/**
 * The bulk action that undoes a panel change: the opposite one, on exactly the people it changed (review R25). Null for
 * any other action, or when nobody changed.
 */
export function panelUndoOf(input: BulkUsersInput, response: BulkUsersResponse): BulkUsersInput | null {
  if (input.action !== 'add_to_panel' && input.action !== 'remove_from_panel') return null;
  const ids = response.results.filter((result) => result.outcome === 'changed').map((result) => result.userId);
  if (ids.length === 0) return null;
  return {
    action: input.action === 'add_to_panel' ? 'remove_from_panel' : 'add_to_panel',
    selection: { kind: 'ids', ids },
  };
}
